import React, { useState } from 'react';
import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Collapse,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/logos/sudarshan-logo.jpg';

/* ─── COLORS ─── */
const BG_COLOR = '#18458b';
const NAV_BG_COLOR = '#18458b';
const CYAN = '#3eb0e9';

/* ─── DATA ─── */
const services = [
    { label: 'Tour Operators', path: '/tour-operators' },
    { label: 'Bus Tickets', path: '/bus-ticket-booking' },
    { label: 'Train Tickets', path: '/train-ticket-booking' },
    { label: 'Flight Tickets', path: '/flight-ticket-booking' },
    { label: 'Car & Coach Rental', path: '/car-coach-rental' },
    { label: 'Organizing School & College Trips', path: '/organizing-school-college-trips' },
    { label: 'Hotel Booking', path: '/hotel-booking' },
];

const packages = [
    { label: 'Tirupati Balaji Sheegra Darshan', path: '/tirupati-balaji-sheegra-darshan' },
    { label: 'Shirdi - Shanisinganapur', path: '/shirdi-shanisinganapur' },
    { label: 'Madurai - Rameshwaram - Kanyakumari', path: '/madurai-rameshwaram-kanyakumari' },
    { label: 'Bangalore Sight Seeing', path: '/bangalore-sight-seeing' },
    { label: 'Mysore Sight Seeing', path: '/mysore-sight-seeing' },
    { label: 'Mysore - OOTY - Coonoor', path: '/mysore-ooty-coonoor' },
    { label: 'OOTY - Kodaikanal', path: '/ooty-kodaikanal' },
    { label: 'Munnar - Thekkady - Alleppey - Cochin', path: '/munnar-thekkady-alleppey-cochin' },
    { label: 'GOA Beach City', path: '/goa-beach-city' },
    { label: 'Pondicherry - Mahabalipuram', path: '/pondicherry-mahabalipuram' },
    { label: 'Coorg With Home Stay', path: '/coorg-with-home-stay' },
    { label: 'Hyderabad Ramoji Film City', path: '/hyderabad-ramoji-film-city' },
    { label: 'Delhi - Agra - Jaipur', path: '/delhi-agra-jaipur' },
    { label: 'Simla - Kullu - Manali', path: '/shimla-kullu-manali' },
];

/* ─── LINK STYLE (same as Navbar) ─── */
const linkSx = {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
    fontFamily: 'Arial, sans-serif',
    px: '18px',
    py: '12px',
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    lineHeight: 1,
    userSelect: 'none',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
    transition: 'background-color 0.15s',
};

/* ─── DROPDOWN — MUI Tooltip (built-in hover, no custom mouse events) ─── */
const NavDropdown = ({ label, items }) => {
    const navigate = useNavigate();

    const dropdownMenu = (
        <Box
            sx={{
                backgroundColor: NAV_BG_COLOR,
                border: '1px solid rgba(255,255,255,0.15)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                minWidth: 280,
                py: 0,
            }}
        >
            {items.map((item, idx) => (
                <Box
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    sx={{
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontFamily: 'Arial, sans-serif',
                        py: '9px',
                        px: '16px',
                        cursor: 'pointer',
                        borderBottom: idx < items.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                        '&:hover': { backgroundColor: CYAN },
                    }}
                >
                    {item.label}
                </Box>
            ))}
        </Box>
    );

    return (
        <Tooltip
            title={dropdownMenu}
            placement="bottom-start"
            enterDelay={0}
            leaveDelay={100}
            arrow={false}
            slotProps={{
                tooltip: {
                    sx: {
                        backgroundColor: 'transparent',
                        p: 0,
                        maxWidth: 'none',
                        borderRadius: 0,
                    },
                },
                popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, -14] } }],
                },
            }}
        >
            <Box component="span" sx={{ ...linkSx }}>
                {label}
                <ExpandMoreIcon sx={{ fontSize: '0.9rem', ml: '4px' }} />
            </Box>
        </Tooltip>
    );
};

/* ─── MOBILE SECTION (same as Navbar) ─── */
const MobileSection = ({ label, items, onClose }) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const isChildActive = items.some(item => item.path === currentPath);
    const [open, setOpen] = useState(isChildActive);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (isChildActive) {
            setOpen(true);
        }
    }, [isChildActive]);

    return (
        <>
            <ListItem
                button
                onClick={() => setOpen(!open)}
                sx={{ 
                    color: isChildActive ? CYAN : '#fff', 
                    bgcolor: isChildActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } 
                }}
            >
                <ListItemText
                    primary={label}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' }}
                />
                {open ? <ExpandLessIcon sx={{ color: isChildActive ? CYAN : '#fff' }} /> : <ExpandMoreIcon sx={{ color: isChildActive ? CYAN : '#fff' }} />}
            </ListItem>
            <Collapse in={open}>
                <List disablePadding>
                    {items.map((item) => {
                        const isActive = item.path === currentPath;
                        return (
                        <ListItem
                            button
                            key={item.label}
                            onClick={() => {
                                navigate(item.path);
                                onClose();
                            }}
                            component="a"
                            href={item.href}
                            sx={{ 
                                pl: 4, 
                                color: isActive ? CYAN : '#cce', 
                                bgcolor: isActive ? 'rgba(62,176,233,0.18)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(62,176,233,0.18)' } 
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{ fontSize: '0.82rem', fontFamily: 'Arial, sans-serif' }}
                            />
                        </ListItem>
                        );
                    })}
                </List>
            </Collapse>
        </>
    );
};

/* ─── MAIN HEADER ─── */
const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {/* TOP INFO BAR - exactly like TopInfoBar component */}
            <Box sx={{ backgroundColor: BG_COLOR, width: '100%', display: { xs: 'none', md: 'block' } }}>
                <Box
                    sx={{
                        maxWidth: '1200px',
                        mx: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 58,
                    }}
                >
                    {/* LOGO - Left Side */}
                    <Box
                        component="a"
                        onClick={() => navigate('/')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            flexShrink: 0,
                            cursor: 'pointer'
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="Sudarshan Travels & Holidays"
                            sx={{ height: 52, width: 'auto', objectFit: 'contain', display: 'block' }}
                        />
                    </Box>

                    {/* Right Side - Contact Info */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                    }}>
                        {/* Phone Number */}
                        <Typography
                            sx={{
                                color: '#fff',
                                fontSize: '1rem',
                                fontWeight: 800,
                                fontFamily: 'Arial, sans-serif',
                                letterSpacing: '0.4px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            9535681900 / 7019927779
                        </Typography>

                        {/* Vertical Line */}
                        <Box
                            sx={{
                                width: '1px',
                                height: 58,
                                backgroundColor: '#12366d',
                                display: 'block',
                            }}
                        />

                        {/* Email */}
                        <Typography sx={{
                            color: '#fff',
                            fontSize: '0.93rem',
                            fontFamily: 'Arial, sans-serif',
                            whiteSpace: 'nowrap',
                            fontWeight: 700,
                        }}>
                            sudarshanholidays101@gmail.com
                        </Typography>



                        {/* Contact Now Button */}
                        <Box
                            component="a"
                            onClick={() => navigate('/contact-us')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: CYAN,
                                color: '#fff',
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                fontFamily: 'Arial, sans-serif',
                                px: '24px',
                                py: '17.5px',
                                flexShrink: 0,
                                textDecoration: 'none',
                                cursor: 'pointer',
                                minWidth: 138,
                                '&:hover': { backgroundColor: '#2a9fd4' },
                                transition: 'background-color 0.2s',
                            }}
                        >
                            Contact Now
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* NAVBAR - exactly like Navbar component */}
            <Box
                component="nav"
                sx={{
                    backgroundColor: NAV_BG_COLOR,
                    width: '100%',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    borderTop: "1px solid #12366d",
                    borderBottom: "1px solid #12366d"
                }}
            >
                {/* Centered content container — same max-width as top bar */}
                <Box
                    sx={{
                        maxWidth: '1200px',
                        mx: 'auto',
                        display: 'flex',
                        alignItems: 'stretch',
                        minHeight: 50,
                    }}
                >
                    {isMobile ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: 1, py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#fff', p: 1 }}>
                                    <MenuIcon sx={{ fontSize: '2rem' }} />
                                </IconButton>
                            </Box>
                            <Box
                                component="img"
                                src={logo}
                                onClick={() => navigate('/')}
                                alt="Sudarshan Travels & Holidays"
                                sx={{ height: 45, width: 'auto', objectFit: 'contain', cursor: 'pointer', borderRadius: '4px' }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
                            {/* Home icon */}
                            <Box component="a" onClick={() => navigate("/")} sx={{ ...linkSx, px: '14px', borderLeft: "1px solid #12366d", borderRight: "1px solid #12366d" }}>
                                <HomeIcon sx={{ color: '#f2b827', fontSize: '1.3rem' }} />
                            </Box>

                            {/* About Us */}
                            <Box component="a" onClick={() => navigate("/about-us")} sx={linkSx}>
                                About Us
                            </Box>

                            {/* Our Services dropdown */}
                            <NavDropdown label="Our Services" items={services} />

                            {/* Tour Packages dropdown */}
                            <NavDropdown label="Tour Packages" items={packages} />

                            {/* Contact Now */}
                            <Box component="a" onClick={() => navigate("/contact-us")} sx={linkSx}>
                                Contact Now
                            </Box>

                            {/* GST — plain white text */}
                            <Box sx={{ display: 'flex', alignItems: 'center', px: '18px' }}>
                                <Typography sx={{
                                    color: '#fff',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    fontFamily: 'Arial, sans-serif',
                                    whiteSpace: 'nowrap',
                                }}>
                                    GST - 29AHKPJ9280E1ZY
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Mobile Drawer - exactly like Navbar */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 290, bgcolor: NAV_BG_COLOR, height: '100%', pt: 1 }}>
                    <List disablePadding>
                        <ListItem
                            button
                            component="a"
                            onClick={() => { navigate('/'); setDrawerOpen(false) }}
                            sx={{ 
                                color: currentPath === '/' ? CYAN : '#fff', 
                                bgcolor: currentPath === '/' ? 'rgba(255,255,255,0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } 
                            }}
                        >
                            <HomeIcon sx={{ color: currentPath === '/' ? CYAN : '#f2b827', mr: 1.5, fontSize: '1.2rem' }} />
                            <ListItemText
                                primary="Home"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' }}
                            />
                        </ListItem>

                        <ListItem
                            button
                            component="a"
                            onClick={() => { navigate("/about-us"); setDrawerOpen(false) }}
                            sx={{ 
                                color: currentPath === '/about-us' ? CYAN : '#fff', 
                                bgcolor: currentPath === '/about-us' ? 'rgba(255,255,255,0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } 
                            }}
                        >
                            <ListItemText
                                primary="About Us"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' }}
                            />
                        </ListItem>

                        <MobileSection label="Our Services" items={services} onClose={() => setDrawerOpen(false)} />
                        <MobileSection label="Tour Packages" items={packages} onClose={() => setDrawerOpen(false)} />

                        <ListItem
                            button
                            component="a"
                            onClick={() => { navigate("/contact-us"); setDrawerOpen(false) }}
                            sx={{ 
                                color: currentPath === '/contact-us' ? CYAN : '#fff', 
                                bgcolor: currentPath === '/contact-us' ? 'rgba(255,255,255,0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } 
                            }}
                        >
                            <ListItemText
                                primary="Contact Now"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' }}
                            />
                        </ListItem>

                        <ListItem sx={{ mt: 2, flexDirection: 'column', alignItems: 'flex-start', gap: 1, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 2 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 800, fontFamily: 'Arial, sans-serif' }}>
                                📞 9535681900 / 7019927779
                            </Typography>
                            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, fontFamily: 'Arial, sans-serif' }}>
                                ✉️ sudarshanholidays101@gmail.com
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'Arial, sans-serif' }}>
                                GST - 29AHKPJ9280E1ZY
                            </Typography>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Header;